/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { AuditLogEventUnit } from '@atlassian/audit-logs-common';

import { DetailsRow } from '../../../../../common/utils/components/details-row';
import { DetailsSection } from '../../../../../common/utils/components/details-section';

import { UnitIdRow } from './unit-id-row';
import { UnitProfileLink } from './unit-profile-link';

const messages = defineMessages({
	unit: {
		id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.title',
		defaultMessage: 'Unit',
		description: 'Title for the unit section in audit log details panel',
	},
	name: {
		id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.name.label',
		defaultMessage: 'Name',
		description: 'Label for the unit name field in audit log unit section',
	},
	unitId: {
		id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.unit-id.label',
		defaultMessage: 'Unit ID',
		description: 'Label for the unit ID field in audit log unit section',
	},
	unitProfile: {
		id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.unit-profile.label',
		defaultMessage: 'Unit profile',
		description: 'Label for the unit profile link field in audit log unit section',
	},
});

interface UnitSectionProps {
	unit?: AuditLogEventUnit;
}

export const UnitSection = ({ unit }: UnitSectionProps) => {
	const { formatMessage } = useIntl();

	if (!unit) {
		return null;
	}

	const { name, id, profile } = unit;

	if (!name && !id && !profile) {
		return null;
	}

	return (
		<DetailsSection title={formatMessage(messages.unit)}>
			<DetailsRow label={formatMessage(messages.name)} value={name} />
			{id && (
				<DetailsRow label={formatMessage(messages.unitId)} value={<UnitIdRow unitId={id} />} />
			)}
			{profile && (
				<DetailsRow
					label={formatMessage(messages.unitProfile)}
					value={<UnitProfileLink profileUrl={profile} />}
				/>
			)}
		</DetailsSection>
	);
};
